import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Zap, Clock, Activity } from 'lucide-react';

interface PerformanceMetrics {
  lastUpdateTime: number;
  averageUpdateTime: number;
  totalUpdates: number;
  fastUpdates: number;
  aiUpdates: number;
}

export function PerformanceMonitor() {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    lastUpdateTime: 0,
    averageUpdateTime: 0,
    totalUpdates: 0,
    fastUpdates: 0,
    aiUpdates: 0
  });

  const [isVisible, setIsVisible] = useState(false);

  // Listen for update events
  useEffect(() => {
    const handleFastUpdate = (event: CustomEvent) => {
      const updateTime = event.detail.duration || 10;
      setMetrics(prev => ({
        ...prev,
        lastUpdateTime: updateTime,
        totalUpdates: prev.totalUpdates + 1,
        fastUpdates: prev.fastUpdates + 1,
        averageUpdateTime: ((prev.averageUpdateTime * prev.totalUpdates) + updateTime) / (prev.totalUpdates + 1)
      }));
      setIsVisible(true);
      setTimeout(() => setIsVisible(false), 3000);
    };

    const handleAIUpdate = (event: CustomEvent) => {
      const updateTime = event.detail.duration || 2000;
      setMetrics(prev => ({
        ...prev,
        lastUpdateTime: updateTime,
        totalUpdates: prev.totalUpdates + 1,
        aiUpdates: prev.aiUpdates + 1,
        averageUpdateTime: ((prev.averageUpdateTime * prev.totalUpdates) + updateTime) / (prev.totalUpdates + 1)
      }));
      setIsVisible(true);
      setTimeout(() => setIsVisible(false), 5000);
    };

    window.addEventListener('plot-fast-update', handleFastUpdate as EventListener);
    window.addEventListener('plot-ai-update', handleAIUpdate as EventListener);

    return () => {
      window.removeEventListener('plot-fast-update', handleFastUpdate as EventListener);
      window.removeEventListener('plot-ai-update', handleAIUpdate as EventListener);
    };
  }, []);

  if (!isVisible && metrics.totalUpdates === 0) return null;

  const fastPercentage = metrics.totalUpdates > 0 ? (metrics.fastUpdates / metrics.totalUpdates) * 100 : 0;

  return (
    <div className={`fixed bottom-4 right-4 bg-card border border-border rounded-lg p-3 shadow-lg transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Activity className="h-4 w-4 text-green-500" />
        <span className="text-sm font-medium">Performance</span>
      </div>
      
      <div className="space-y-2 text-xs">
        <div className="flex items-center gap-2">
          <Zap className="h-3 w-3 text-yellow-500" />
          <span>Last update:</span>
          <Badge variant={metrics.lastUpdateTime < 100 ? "default" : "secondary"}>
            {metrics.lastUpdateTime}ms
          </Badge>
        </div>
        
        <div className="flex items-center gap-2">
          <Clock className="h-3 w-3 text-blue-500" />
          <span>Average:</span>
          <Badge variant="outline">
            {Math.round(metrics.averageUpdateTime)}ms
          </Badge>
        </div>
        
        <div className="flex items-center gap-2">
          <span>Fast updates:</span>
          <Badge variant="default" className="bg-green-100 text-green-800">
            {Math.round(fastPercentage)}%
          </Badge>
        </div>
        
        <div className="text-muted-foreground">
          {metrics.fastUpdates} fast • {metrics.aiUpdates} AI
        </div>
      </div>
    </div>
  );
}
